import React, { forwardRef, useEffect, useRef, useState, useMemo } from 'react';

import { Typography, Box } from '@material-ui/core';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { TwitterShareButton, FacebookShareButton, LinkedinShareButton } from 'react-share';

import CloseIcon from '../../assets/icons/CloseIcon';
import FacebookIcon from '../../assets/icons/FacebookIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import MicrosoftTeamsIcon from '../../assets/icons/MicrosoftTeamsIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import { ShareType } from '../../context/TestContext';
import { colorsPalette } from '../../theme';
import { getCourseSocialShareImage, getSocialShareImage, translate } from '../../utils/helpers';
import { IKImage } from '../IKImage';
import { CardWrapper } from '../UI/CardWrapper';
import { ShareButton } from '../UI/ShareButton';
import useStyles from './styles';

type GenerateShareDataInput = {
  type: ShareType;
  resultHash?: string | null;
  tenantName?: string | null;
  entityName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  fbName?: string | null;
  twitterHandle?: string | null;
};

const generateShareData = ({
  type,
  resultHash,
  tenantName,
  entityName,
  cpdCredits,
  category,
  fbName,
  twitterHandle,
}: GenerateShareDataInput) => {
  switch (type) {
    case ShareType.Pathway: {
      const shareText =
        entityName && cpdCredits
          ? translate('page.test.shareText', { pathwayName: entityName, cpdCredits })
          : '';
      const shareTwitterText =
        entityName && category
          ? translate('page.test.shareTwitterText', {
              pathwayName: entityName,
              twitterHandle,
              category,
            })
          : '';
      const shareFacebookText =
        entityName && category
          ? translate('page.test.shareFacebookText', { pathwayName: entityName, fbName, category })
          : '';

      return {
        shareLink: `${window?.location?.origin}/share/${resultHash}`,
        socialShareImgUrl: getSocialShareImage(resultHash, tenantName),
        shareText,
        shareTwitterText,
        shareFacebookText,
      };
    }
    case ShareType.Course: {
      const shareText = entityName
        ? translate('page.course.shareText', { courseName: entityName })
        : '';
      const shareTwitterText = entityName
        ? translate('page.course.shareTwitterText', {
            courseName: entityName,
            twitterHandle,
            category,
          })
        : '';
      const shareFacebookText = entityName
        ? translate('page.course.shareFacebookText', { courseName: entityName, fbName })
        : '';

      return {
        shareLink: `${window?.location?.origin}/share/courses/${resultHash}`,
        socialShareImgUrl: getCourseSocialShareImage(resultHash, tenantName),
        shareText,
        shareTwitterText,
        shareFacebookText,
      };
    }
  }
};

const formatHashTags = (hashtags?: (string | null)[] | null): string[] => {
  const arr = (hashtags ? hashtags.filter((hashtag) => Boolean(hashtag)) : []) as string[];

  return arr.map((hashtag) => (hashtag?.startsWith('#') ? hashtag?.substring(1) : hashtag));
};

interface ShareModalProps {
  handleClose: () => void;
  resultHash?: string | null;
  entityName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  slug?: string | null;
  tenantName?: string | null;
  type?: ShareType;
  fbName?: string | null;
  twitterHandle?: string | null;
  twitterHashtags?: (string | null)[] | null;
}

const ShareModal = forwardRef<HTMLDivElement, ShareModalProps>(
  (
    {
      handleClose,
      resultHash,
      entityName,
      cpdCredits,
      category,
      tenantName,
      type = ShareType.Pathway,
      fbName,
      twitterHandle,
      twitterHashtags,
    },
    ref,
  ) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const teamsRef = useRef<HTMLDivElement | null>(null);
    const [teamsLink, setTeamsLink] = useState('');

    const formattedHashtags = useMemo(() => formatHashTags(twitterHashtags), [twitterHashtags]);

    const {
      shareLink,
      socialShareImgUrl,
      shareText,
      shareFacebookText,
      shareTwitterText,
    } = useMemo(
      () =>
        generateShareData({
          type,
          resultHash,
          tenantName,
          cpdCredits,
          category,
          entityName,
          fbName,
          twitterHandle,
        }),
      [type, resultHash, tenantName, entityName, category, cpdCredits, fbName, twitterHandle],
    );

    const handleOpenTeamsShare = () => {
      window.open(teamsLink, 'Share to Microsoft Teams', 'width=700,height=600');
    };

    useEffect(() => {
      //used to call microsoft library for share link generation
      //@ts-ignore
      window?.shareToMicrosoftTeams?.renderButtons && window.shareToMicrosoftTeams.renderButtons();
    }, []);

    useEffect(() => {
      const child = teamsRef?.current?.children?.item(0);
      //@ts-ignore
      if (child?.href) {
        //@ts-ignore
        setTeamsLink(child?.href);
      }
    }, []);

    return (
      <CardWrapper ref={ref} wrapperClass={classes.wrapper}>
        <Box className={classes.headerWrapper}>
          <Typography variant="h2" className={classes.title}>
            {t('page.test.shareYourSuccess')}
          </Typography>
          <CloseIcon
            color={colorsPalette.gray}
            className={classes.closeIcon}
            onClick={handleClose}
          />
        </Box>
        <Box className={classes.content}>
          <div className={classes.certificateImgWrapper}>
            <div className={classes.relativeWrapper}>
              <div className={classes.certificateImgWrapperWithBg}>
                <IKImage
                  className={classes.certificateImg}
                  path={socialShareImgUrl}
                  alt={t('page.test.share')}
                  srcset={[{ width: 750 }]}
                  sizes="100vw"
                />
              </div>
            </div>
          </div>
          <Box className={classes.buttonsWrapper}>
            <LinkedinShareButton
              className={classes.shareButtonWrapper}
              title={shareText}
              url={shareLink}
              resetButtonStyle={true}
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <LinkedInIcon color={colorsPalette.white} />
                {t('page.test.linkedIn')}
              </ShareButton>
            </LinkedinShareButton>
            <ShareButton wrapperClass={classes.shareButton} onClick={handleOpenTeamsShare}>
              <MicrosoftTeamsIcon color={colorsPalette.white} />
              {t('page.test.microsoftTeams')}
            </ShareButton>
            <FacebookShareButton quote={shareFacebookText} url={shareLink} resetButtonStyle={true}>
              <ShareButton wrapperClass={classes.shareButton}>
                <FacebookIcon color={colorsPalette.white} />
                {t('page.test.facebook')}
              </ShareButton>
            </FacebookShareButton>
            <TwitterShareButton
              className={classes.shareButtonWrapper}
              title={shareTwitterText}
              url={shareLink}
              resetButtonStyle={true}
              hashtags={formattedHashtags}
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <TwitterIcon color={colorsPalette.white} />
                {t('page.test.twitter')}
              </ShareButton>
            </TwitterShareButton>
            <div
              className={cx('teams-share-button', classes.teamsShareOriginal)}
              data-href={shareLink}
              data-msg-text={shareText}
              ref={teamsRef}
            />
          </Box>
        </Box>
      </CardWrapper>
    );
  },
);

export default ShareModal;
